import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticleBySlug, getArticlesByCategory } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <div className="pt-20 px-4 md:px-6 text-center py-20">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">文章未找到</h1>
            <p className="text-foreground-500 mb-6">请检查您访问的文章地址是否正确</p>
            <Link to="/" className="text-primary-500 font-medium hover:text-primary-600">返回首页</Link>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const category = getCategoryBySlug(article.categorySlug);
  const relatedArticles = getArticlesByCategory(article.categorySlug)
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    dateModified: article.updateDate,
    url: `https://www.91.com/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: 'http 91.com 官方指南',
      url: 'https://www.91.com',
    },
    inLanguage: 'zh-CN',
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: article.title,
    description: article.summary,
    url: `https://www.91.com/article/${article.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'http 91.com 官方指南',
      url: 'https://www.91.com',
    },
    mainEntity: {
      '@type': 'Article',
      headline: article.title,
      description: article.summary,
      author: { '@type': 'Person', name: article.author },
      datePublished: article.publishDate,
      dateModified: article.updateDate,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || article.categorySlug, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />
        </div>

        {/* Article Content */}
        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <header className="mb-8">
              <div className="flex items-center flex-wrap gap-2 mb-4">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full font-medium hover:bg-accent-200 transition-colors"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-base text-foreground-500 leading-relaxed mb-4">{article.summary}</p>
              <div className="flex items-center flex-wrap gap-4 text-xs text-foreground-400 border-b border-background-200/60 pb-6">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  发布于 {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-refresh-line"></i>
                  更新于 {article.updateDate}
                </span>
              </div>
            </header>

            {/* Content */}
            <div
              className="prose-content text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            {article.tags.length > 0 && (
              <div className="mt-10 pt-6 border-t border-background-200/60">
                <p className="text-xs text-foreground-400 mb-3">文章标签</p>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-5">相关文章</h2>
              <div className="flex flex-col gap-3">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.slug}`}
                    className="bg-background-50 border border-background-200/60 rounded-xl p-4 transition-all duration-300 hover:border-primary-200 group cursor-pointer"
                  >
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors mb-1">
                      {rel.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-1">{rel.summary}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>

      {/* Article Content Styles */}
      <style>{`
        .prose-content h2 {
          font-size: 1.375rem;
          font-weight: 700;
          color: oklch(var(--foreground-950));
          margin-top: 2rem;
          margin-bottom: 0.75rem;
          line-height: 1.4;
        }
        .prose-content h3 {
          font-size: 1.125rem;
          font-weight: 600;
          color: oklch(var(--foreground-900));
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
          line-height: 1.4;
        }
        .prose-content p {
          font-size: 0.9375rem;
          line-height: 1.85;
          margin-bottom: 1rem;
          color: oklch(var(--foreground-700));
        }
        .prose-content ul, .prose-content ol {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }
        .prose-content ul {
          list-style-type: disc;
        }
        .prose-content ol {
          list-style-type: decimal;
        }
        .prose-content li {
          font-size: 0.9375rem;
          line-height: 1.85;
          margin-bottom: 0.375rem;
          color: oklch(var(--foreground-700));
        }
        .prose-content strong {
          color: oklch(var(--foreground-950));
          font-weight: 600;
        }
      `}</style>
    </>
  );
}